import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });

export const THEME_STORAGE_KEY = 'portfolio-theme';
const THEMES = ['light', 'dark'];

/**
 * Light is the default (professional presentation for recruiters/HR).
 * A stored preference always wins; OS preference is intentionally not
 * followed since the site is moving away from dark mode.
 */
export function getInitialTheme() {
    if (typeof window === 'undefined') return 'light';
    try {
        const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
        if (THEMES.includes(stored)) return stored;
    } catch (e) {
        /* localStorage unavailable (private mode, etc.) — fall through */
    }
    return 'light';
}

export function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(getInitialTheme);

    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute('data-theme', theme);
        root.style.colorScheme = theme;

        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, theme);
        } catch (e) {
            /* ignore storage failures */
        }

        // Keep browser/PWA chrome in sync with the active theme
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) {
            meta.setAttribute('content', theme === 'dark' ? '#0a0a0f' : '#f7f8fa');
        }

        // Let canvas-based visuals (Three.js background, chart canvases)
        // re-read their palette from the CSS custom properties.
        document.dispatchEvent(new CustomEvent('themechange', { detail: { theme } }));
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
    }, []);

    return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    return useContext(ThemeContext);
}
